/**
 * Datasource local AppContent: abstração + implementação localforage (cache com TTL).
 */

import localforage from 'localforage';
import {
  AppContentModel,
  type AppContentCurrentEntity,
  type AppContentKey,
  type AppContentVersionEntity,
} from '../../domain/entities/app-content.entity';

const DEFAULT_CACHE_TTL_MS = 60 * 60 * 1000;

interface Timestamped<T> {
  data: T;
  _ts: number;
}

const store = localforage.createInstance({ name: 'retornaar', storeName: 'app_content' });

function storageKeyCurrent(key: AppContentKey): string {
  return `current:${key}`;
}

function storageKeyVersions(key: AppContentKey): string {
  return `versions:${key}`;
}

function isCacheValid<T>(entry: Timestamped<T> | null | undefined, ttlMs: number): entry is Timestamped<T> {
  if (!entry) return false;
  return Date.now() - entry._ts < ttlMs;
}

const CURRENT_DATE_KEYS = ['createdAt', 'updatedAt', 'publishedAt'] as const;
const VERSION_DATE_KEYS = ['createdAt', 'publishedAt'] as const;

function reviveCurrentForParse(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return {};
  const o = { ...(raw as Record<string, unknown>) };
  for (const k of CURRENT_DATE_KEYS) {
    const v = o[k];
    if (typeof v === 'string') o[k] = new Date(v);
  }
  return o;
}

function reviveVersionForParse(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return {};
  const o = { ...(raw as Record<string, unknown>) };
  for (const k of VERSION_DATE_KEYS) {
    const v = o[k];
    if (typeof v === 'string') o[k] = new Date(v);
  }
  return o;
}

function parseCurrentFromStorage(raw: unknown): AppContentCurrentEntity | null {
  if (raw === null) return null;
  try {
    return AppContentModel.fromCurrentJson(reviveCurrentForParse(raw));
  } catch {
    return null;
  }
}

function parseVersionFromStorage(raw: unknown): AppContentVersionEntity | null {
  try {
    return AppContentModel.fromVersionJson(reviveVersionForParse(raw));
  } catch {
    return null;
  }
}

export interface AppContentLocalDataSource {
  getCurrent(key: AppContentKey): Promise<AppContentCurrentEntity | null | undefined>;
  getVersions(key: AppContentKey): Promise<AppContentVersionEntity[] | undefined>;
  setCurrentVersion(key: AppContentKey, entity: AppContentCurrentEntity): Promise<void>;
  addOldVersion(key: AppContentKey, entity: AppContentVersionEntity): Promise<void>;
  setCurrent(key: AppContentKey, entity: AppContentCurrentEntity | null): Promise<void>;
  setVersions(key: AppContentKey, versions: AppContentVersionEntity[]): Promise<void>;
  invalidate(key?: AppContentKey): Promise<void>;
}

export class AppContentLocalForageDataSource implements AppContentLocalDataSource {
  constructor(private readonly cacheTtlMs: number = DEFAULT_CACHE_TTL_MS) {}

  async getCurrent(key: AppContentKey): Promise<AppContentCurrentEntity | null | undefined> {
    const entry = await store.getItem<Timestamped<unknown>>(storageKeyCurrent(key));
    if (!isCacheValid(entry, this.cacheTtlMs)) return undefined;
    return parseCurrentFromStorage(entry.data);
  }

  async getVersions(key: AppContentKey): Promise<AppContentVersionEntity[] | undefined> {
    const entry = await store.getItem<Timestamped<unknown[]>>(storageKeyVersions(key));
    if (!isCacheValid(entry, this.cacheTtlMs)) return undefined;
    const list = entry.data
      .map((raw) => parseVersionFromStorage(raw))
      .filter((v): v is AppContentVersionEntity => v !== null);
    return list;
  }

  async setCurrentVersion(key: AppContentKey, entity: AppContentCurrentEntity): Promise<void> {
    await this.writeCurrent(key, entity);
  }

  async addOldVersion(key: AppContentKey, entity: AppContentVersionEntity): Promise<void> {
    const entry = await store.getItem<Timestamped<unknown[]>>(storageKeyVersions(key));
    const currentList =
      entry && isCacheValid(entry, this.cacheTtlMs)
        ? entry.data
            .map((raw) => parseVersionFromStorage(raw))
            .filter((v): v is AppContentVersionEntity => v !== null)
        : [];
    const dedup = currentList.filter((v) => v.version !== entity.version);
    const next = [entity, ...dedup].sort((a, b) => b.version - a.version);
    await store.setItem(storageKeyVersions(key), {
      data: next.map((v) => ({ ...v })),
      _ts: Date.now(),
    });
  }

  async setCurrent(key: AppContentKey, entity: AppContentCurrentEntity | null): Promise<void> {
    await this.writeCurrent(key, entity);
  }

  async setVersions(key: AppContentKey, versions: AppContentVersionEntity[]): Promise<void> {
    await store.setItem(storageKeyVersions(key), {
      data: versions.map((v) => ({ ...v })),
      _ts: Date.now(),
    });
  }

  async invalidate(key?: AppContentKey): Promise<void> {
    if (key) {
      await store.removeItem(storageKeyCurrent(key));
      await store.removeItem(storageKeyVersions(key));
      return;
    }
    await store.clear();
  }

  private async writeCurrent(
    key: AppContentKey,
    entity: AppContentCurrentEntity | null
  ): Promise<void> {
    await store.setItem(storageKeyCurrent(key), {
      data: entity === null ? null : { ...entity },
      _ts: Date.now(),
    });
  }
}
